import { useState, useEffect, useCallback } from "react";
import {
  signInWithGoogle,
  signOutUser,
  getCurrentUser,
  onAuthStateChange,
  convertToAuthUser,
  AuthUser,
} from "@/services/firebase/auth";

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // 刷新用戶權限（強制刷新 token 以獲取最新的 Custom Claims）
  const refreshUserPermissions = useCallback(async () => {
    const firebaseUser = getCurrentUser();
    if (firebaseUser) {
      try {
        const authUser = await convertToAuthUser(firebaseUser, true); // 強制刷新 token
        setUser(authUser);
        return authUser;
      } catch (error) {
        console.error("Error refreshing user permissions:", error);
        return null;
      }
    }
    return null;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // 首次載入時強制刷新 token 以確保獲取最新的 Custom Claims
          const authUser = await convertToAuthUser(firebaseUser, true);
          setUser(authUser);
        } catch (error) {
          console.error("Error converting user:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async () => {
    try {
      await signInWithGoogle();
      // 登入後等待一下，讓 onAuthStateChange 觸發並刷新 token
      // 狀態會通過 onAuthStateChange 自動更新
      await new Promise(resolve => setTimeout(resolve, 500));
      // 再次刷新以確保獲取最新 Custom Claims
      await refreshUserPermissions();
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await signOutUser();
      setUser(null);
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  return {
    user,
    loading,
    signIn,
    signOut,
    refreshUserPermissions,
    isAuthenticated: !!user,
  };
}

